import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Header from './components/Header';
import LoginPage from './LoginPage';
import SignupPage from './SignupPage';
import ProfilePage from './ProfilePage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import MatchListPage from './MatchListPage';
import NewMatchPage from './NewMatchPage';
import MatchLivePage from './MatchLivePage';
import StatsPage from './components/StatsPage';
import SpectatorView from './SpectatorView';
import './App.css';
import CourtZonesTest from './CourtZonesTest';
import { enableAutoSync } from './services/matchService';
import RecoveryPage from './RecoveryPage';
import VideoListPage from './components/expert/VideoListPage';
import PlayersPage from './components/PlayersPage';

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  
  const [currentMatch, setCurrentMatch] = useState(null);
  const [mode, setMode] = useState('LIST');
  const [hideHeader, setHideHeader] = useState(false);
  const [currentScore, setCurrentScore] = useState(null);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // 🆕 Nettoyer le localStorage des matchs des autres utilisateurs
  const cleanLocalStorageForUser = (userId) => {
    console.log('🧹 Nettoyage localStorage pour userId:', userId.substring(0, 8));

    // Nettoyer la liste globale
    const matches = JSON.parse(localStorage.getItem('matches') || '[]');
    const filtered = matches.filter(m => m.userId === userId || !m.userId);

    if (filtered.length !== matches.length) {
      console.log(`🧹 Suppression de ${matches.length - filtered.length} matchs étrangers`);
      localStorage.setItem('matches', JSON.stringify(filtered));
    }

    // Nettoyer les fichiers individuels
    Object.keys(localStorage)
      .filter(k => k.startsWith('match_'))
      .forEach(key => {
        try {
          const match = JSON.parse(localStorage.getItem(key));
          if (match.userId && match.userId !== userId) {
            console.log(`🧹 Suppression fichier étranger: ${key}`);
            localStorage.removeItem(key);
          }
        } catch (e) {
          // Fichier corrompu → supprimer
          localStorage.removeItem(key);
        }
      });
  };

  // Timeout de sécurité pour éviter le blocage sur "Chargement..."
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('⚠️ Timeout auth - déblocage forcé');
      setAuthLoading(false);
    }, 5000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      // 🆕 Nettoyer au chargement initial si utilisateur déjà connecté
      if (session?.user) {
        cleanLocalStorageForUser(session.user.id);
      }
      const u = session?.user ?? null;
      setUser(u);
      if (u && !u.email_confirmed_at) {
        setEmailNotConfirmed(true);
      }
      setAuthLoading(false);
    }).catch((err) => {
      console.error('❌ Erreur getSession:', err);
      clearTimeout(timeout);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth event:', event);

      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔑 PASSWORD_RECOVERY détecté, redirection vers /reset-password');
        window.location.href = '/reset-password';
        return;
      }

      // 🆕 Nettoyer le localStorage à chaque connexion
      if (event === 'SIGNED_IN' && session?.user) {
        cleanLocalStorageForUser(session.user.id);
        if (!session.user.email_confirmed_at) {
          setEmailNotConfirmed(true);
        } else {
          setEmailNotConfirmed(false);
        }
      }

      if (event === 'USER_UPDATED' && session?.user?.email_confirmed_at) {
        setEmailNotConfirmed(false);
      }

      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    enableAutoSync();
  }, []);

  const handleResendEmail = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
    });
    setResendLoading(false);
    if (!error) setResendSuccess(true);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCurrentMatch(null);
    setMode('LIST');
    setHideHeader(false);
  };

  const handleOpenMatch = (match) => {
    setCurrentMatch(match);
    setMode('LIVE');
  };

  const handleNewMatch = () => setMode('NEW');

  const handleMatchCreated = (newMatch, isEdit = false) => {
    console.log('Match créé/modifié', newMatch, 'isEdit:', isEdit);

    if (isEdit) {
      setCurrentMatch(null);
      setMode('LIST');
    } else {
      setCurrentMatch(newMatch);
      setMode('LIVE');
    }
  };

  const handleEditMatch = (match) => {
    setCurrentMatch(match);
    setMode('EDIT');
  };

  const handleViewStats = (match) => {
    console.log('📊 handleViewStats - Match reçu:', match.id);

    // 🆕 Lire le fichier individuel qui contient l'engineState complet avec les points
    // (la liste globale peut ne pas avoir les points si le cloud n'est pas encore à jour)
    const matchId = match.id || match.matchId;
    const fileData = localStorage.getItem(`match_${matchId}`);
    const fullMatch = fileData ? { ...match, ...JSON.parse(fileData) } : match;

    console.log('📊 Points (liste):', match.engineState?.points?.length);
    console.log('📊 Points (fichier local):', fullMatch.engineState?.points?.length);

    if (!fullMatch.engineState || !fullMatch.engineState.points || fullMatch.engineState.points.length === 0) {
      console.error('❌ Match sans points détaillés');
      alert("⚠️ Ce match ne contient pas de données de points détaillées.\n\nImpossible d'afficher les statistiques.");
      return;
    }

    console.log('✅ Match complet avec', fullMatch.engineState.points.length, 'points');

    setCurrentMatch(fullMatch);
    setMode('STATS');
  };

  const handleTestZones = () => {
    if (currentMatch?.matchId) {
      const key = `match_${currentMatch.matchId}`;
      const dataStr = localStorage.getItem(key);

      let scoreToUse = null;

      if (dataStr) {
        const matchData = JSON.parse(dataStr);
        if (matchData.currentScore) {
          scoreToUse = matchData.currentScore;
          console.log("🎯 Mode Expert - Score chargé depuis localStorage:", scoreToUse);
        }
      }

      if (!scoreToUse) {
        scoreToUse = {
          jeuScore: "0-0",
          sets: [[0, 0]],
          server: currentMatch.premierServeur || "J1",
          tieBreakActive: false,
          superTieBreakActive: false,
          totalPoints: 0,
          lastTieBreakScores: []
        };
        console.log("🎯 Mode Expert - Score initial créé:", scoreToUse);
      }

      setCurrentScore(scoreToUse);
    }
    setMode('TEST_ZONES');
  };

  const handleOrientationChange = (orientation) => {
    setHideHeader(orientation === 'landscape');
  };

  const handleNavigateVideos = () => setMode('VIDEOS');
  const handleNavigatePlayers = () => setMode('PLAYERS');

  const handleBackToList = () => {
    setMode('LIST');
    setCurrentMatch(null);
    setHideHeader(false);
  };

  const handleOpenProfile = () => {
    setMode('PROFILE');
  };

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize: '18px',
        color: '#666'
      }}>
        Chargement...
      </div>
    );
  }

  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/spectator/:matchId" element={<SpectatorView />} />
          <Route path="/recovery" element={<RecoveryPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/*" element={
            <>
              {authMode === 'login' && (
                <LoginPage
                  onSignupClick={() => setAuthMode('signup')}
                  onForgotPasswordClick={() => setAuthMode('forgot')}
                />
              )}
              {authMode === 'signup' && (
                <SignupPage
                  onLoginClick={() => setAuthMode('login')}
                />
              )}
              {authMode === 'forgot' && (
                <ForgotPasswordPage
                  onBackToLogin={() => setAuthMode('login')}
                />
              )}
            </>
          } />
        </Routes>
      </Router>
    );
  }

  // Écran email non confirmé
  if (user && emailNotConfirmed) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px',
        fontFamily: 'Arial, sans-serif', backgroundColor: '#f5f5f5',
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '32px',
          maxWidth: '400px', width: '100%', textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📧</div>
          <h2 style={{ margin: '0 0 8px', fontSize: '20px', color: '#1a1a2e' }}>
            Validez votre email
          </h2>
          <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.5', margin: '0 0 24px' }}>
            Un email de validation a été envoyé à <strong>{user.email}</strong>.
            Cliquez sur le lien dans l'email pour activer votre compte.
          </p>

          {resendSuccess && (
            <div style={{
              backgroundColor: '#e8f5e9', color: '#388E3C', borderRadius: '8px',
              padding: '10px', fontSize: '13px', marginBottom: '16px',
            }}>
              ✅ Email renvoyé avec succès !
            </div>
          )}

          <button
            onClick={handleResendEmail}
            disabled={resendLoading}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px',
              backgroundColor: '#1976D2', color: 'white', border: 'none',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              marginBottom: '12px', opacity: resendLoading ? 0.7 : 1,
            }}
          >
            {resendLoading ? 'Envoi...' : "📨 Renvoyer l'email de validation"}
          </button>

          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px',
              backgroundColor: 'transparent', color: '#999', border: '1px solid #ddd',
              fontSize: '14px', cursor: 'pointer',
            }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  console.log('🎨 App.js RENDER - Mode actuel:', mode);
  console.log('🎨 currentMatch:', currentMatch?.id);

  return (
    <Router>
      <Routes>
        <Route path="/spectator/:matchId" element={<SpectatorView />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/*" element={
          <div className="App">
            <Header
              currentUser={user}
              onLogout={handleLogout}
              onOpenProfile={handleOpenProfile}
              onOpenPlayers={handleNavigatePlayers}
              showUserInfo={true}
              hideHeader={hideHeader}
            />

            {mode === 'NEW' && (
              <NewMatchPage
                currentUser={user}
                onCancel={handleBackToList}
                onMatchCreated={handleMatchCreated}
              />
            )}

            {mode === 'LIVE' && (currentMatch?.id || currentMatch?.matchId) && (
              <MatchLivePage
                currentUser={user}
                match={currentMatch}
                onBack={handleBackToList}
                onTestZones={handleTestZones}
                onScoreUpdate={setCurrentScore}
              />
            )}

            {mode === 'EDIT' && (
              <NewMatchPage
                currentUser={user}
                onCancel={handleBackToList}
                onMatchCreated={handleMatchCreated}
                currentMatch={currentMatch}
              />
            )}

            {mode === 'STATS' && currentMatch && (
              <StatsPage
                match={currentMatch}
                onBack={handleBackToList}
              />
            )}

            {mode === 'TEST_ZONES' && currentMatch && (
              <CourtZonesTest
                match={currentMatch}
                currentScore={currentScore}
                onBack={handleBackToList}
                onOrientationChange={handleOrientationChange}
              />
            )}

            {mode === 'PROFILE' && (
              <ProfilePage
                user={user}
                onBack={handleBackToList}
                onLogout={handleLogout}
              />
            )}

            {mode === 'VIDEOS' && (
              <VideoListPage
                currentUser={user}
                onBack={handleBackToList}
              />
            )}

            {mode === 'PLAYERS' && (
              <PlayersPage
                currentUser={user}
                onBack={handleBackToList}
                isAdmin={user?.app_metadata?.role === 'admin'}
              />
            )}

            {mode === 'LIST' && (
              <MatchListPage
                currentUser={user}
                onLogout={handleLogout}
                onOpenMatch={handleOpenMatch}
                onNewMatch={handleNewMatch}
                onEditMatch={handleEditMatch}
                onViewStats={handleViewStats}
                onNavigateVideos={handleNavigateVideos}
                onNavigatePlayers={handleNavigatePlayers}
              />
            )}
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;